import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Stack,
  Alert,
  IconButton,
  Tooltip,
  CircularProgress,
  Paper,
  Chip,
  Grid,
  InputAdornment,
  Divider
} from '@mui/material';
import {
  Search as SearchIcon,
  Add as AddIcon,
  OpenInNew as OpenInNewIcon,
  Work as WorkIcon,
  LocationOn as LocationIcon,
  Business as BusinessIcon,
  AccessTime as AccessTimeIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { searchJobs } from '../services/api';

interface JobResult {
  title: string;
  link: string;
  company: string;
  location: string;
  postedDate: string;
}

const ComingSoon: React.FC = () => (
  <Card elevation={0} sx={{ 
    border: '1px solid',
    borderColor: 'divider',
    borderRadius: 2,
    overflow: 'hidden',
    textAlign: 'center',
    p: 4
  }}>
    <CardContent>
      <Typography variant="h4" color="primary" gutterBottom>
        🚧
      </Typography>
      <Typography variant="h5" fontWeight="600" gutterBottom>
        Job Search
      </Typography>
      <Typography variant="body1" color="text.secondary">
        This feature is coming soon!
      </Typography>
    </CardContent>
  </Card>
);

const JobSearch: React.FC = () => {
  const [jobRoles, setJobRoles] = useState<string[]>([]);
  const [keywords, setKeywords] = useState<string[]>([]);
  const [newJobRole, setNewJobRole] = useState('');
  const [newKeyword, setNewKeyword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [results, setResults] = useState<JobResult[]>([]);
  const [showComingSoon, setShowComingSoon] = useState(true);

  const handleAddJobRole = () => {
    if (newJobRole.trim()) {
      setJobRoles([...jobRoles, newJobRole.trim()]);
      setNewJobRole('');
    }
  };

  const handleAddKeyword = () => {
    if (newKeyword.trim()) {
      setKeywords([...keywords, newKeyword.trim()]);
      setNewKeyword('');
    }
  };

  const handleRemoveJobRole = (index: number) => {
    setJobRoles(jobRoles.filter((_, i) => i !== index));
  };

  const handleRemoveKeyword = (index: number) => {
    setKeywords(keywords.filter((_, i) => i !== index));
  };

  const handleSearch = async () => {
    if (jobRoles.length === 0) {
      setError('Please add at least one job role');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const searchResults = await searchJobs(jobRoles, keywords);
      setResults(searchResults);
    } catch (err) {
      setError('Failed to search for jobs');
      console.error('Error searching jobs:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', p: 3 }}>
      {showComingSoon ? (
        <ComingSoon />
      ) : (
        <>
          <Card elevation={0} sx={{ 
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 2,
            overflow: 'hidden',
            mb: 3
          }}>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 3 }}>
                <WorkIcon color="primary" />
                <Typography variant="h5" fontWeight="600">
                  Find Jobs
                </Typography>
              </Stack>

              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Stack spacing={2}>
                    <TextField
                      fullWidth
                      label="Job Roles"
                      value={newJobRole}
                      onChange={(e) => setNewJobRole(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && handleAddJobRole()}
                      placeholder="Add job role..."
                      variant="outlined"
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <Button
                              variant="contained"
                              onClick={handleAddJobRole}
                              startIcon={<AddIcon />}
                              size="small"
                            >
                              Add
                            </Button>
                          </InputAdornment>
                        ),
                      }}
                    />
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {jobRoles.map((role, index) => (
                        <Chip
                          key={index}
                          label={role}
                          onDelete={() => handleRemoveJobRole(index)}
                          color="primary"
                          variant="outlined"
                        />
                      ))}
                    </Box>
                  </Stack>
                </Grid>

                <Grid item xs={12}>
                  <Stack spacing={2}>
                    <TextField
                      fullWidth
                      label="Keywords"
                      value={newKeyword}
                      onChange={(e) => setNewKeyword(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && handleAddKeyword()}
                      placeholder="Add keyword..."
                      variant="outlined"
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <Button
                              variant="contained"
                              onClick={handleAddKeyword}
                              startIcon={<AddIcon />}
                              size="small"
                            >
                              Add
                            </Button>
                          </InputAdornment>
                        ),
                      }}
                    />
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {keywords.map((keyword, index) => (
                        <Chip
                          key={index}
                          label={keyword}
                          onDelete={() => handleRemoveKeyword(index)}
                          color="secondary"
                          variant="outlined"
                        />
                      ))}
                    </Box>
                  </Stack>
                </Grid>

                <Grid item xs={12}>
                  <Button
                    fullWidth
                    variant="contained"
                    size="large"
                    onClick={handleSearch}
                    disabled={loading || jobRoles.length === 0}
                    startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SearchIcon />}
                    sx={{ py: 1.5 }}
                  >
                    {loading ? 'Searching...' : 'Search Jobs'}
                  </Button>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {error && (
            <Alert 
              severity="error" 
              sx={{ mb: 3 }}
              action={
                <IconButton
                  aria-label="close"
                  color="inherit"
                  size="small"
                  onClick={() => setError(null)}
                >
                  <CloseIcon fontSize="inherit" />
                </IconButton>
              }
            >
              {error}
            </Alert>
          )}

          {results.length > 0 && (
            <Card elevation={0} sx={{ 
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2,
              overflow: 'hidden'
            }}>
              <CardContent>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 3 }}>
                  <WorkIcon color="primary" />
                  <Typography variant="h5" fontWeight="600">
                    Search Results
                  </Typography>
                </Stack>

                <Stack spacing={2}>
                  {results.map((result, index) => (
                    <Paper
                      key={index}
                      elevation={0}
                      sx={{
                        p: 2,
                        bgcolor: 'background.default',
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 1,
                        '&:hover': {
                          borderColor: 'primary.main',
                          bgcolor: 'action.hover',
                        },
                      }}
                    >
                      <Grid container spacing={2} alignItems="center">
                        <Grid item xs>
                          <Typography
                            variant="h6"
                            component="a"
                            href={result.link}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{
                              color: 'primary.main',
                              textDecoration: 'none',
                              '&:hover': {
                                textDecoration: 'underline',
                              },
                            }}
                          >
                            {result.title}
                          </Typography>
                          <Stack direction="row" spacing={2} sx={{ mt: 1 }}>
                            <Stack direction="row" spacing={1} alignItems="center">
                              <BusinessIcon fontSize="small" color="action" />
                              <Typography variant="body2" color="text.secondary">
                                {result.company}
                              </Typography>
                            </Stack>
                            <Stack direction="row" spacing={1} alignItems="center">
                              <LocationIcon fontSize="small" color="action" />
                              <Typography variant="body2" color="text.secondary">
                                {result.location}
                              </Typography>
                            </Stack>
                            <Stack direction="row" spacing={1} alignItems="center">
                              <AccessTimeIcon fontSize="small" color="action" />
                              <Typography variant="body2" color="text.secondary">
                                Posted: {result.postedDate}
                              </Typography>
                            </Stack>
                          </Stack>
                        </Grid>
                        <Grid item>
                          <Tooltip title="Open in new tab">
                            <IconButton
                              onClick={() => window.open(result.link, '_blank')}
                              color="primary"
                            >
                              <OpenInNewIcon />
                            </IconButton>
                          </Tooltip>
                        </Grid>
                      </Grid>
                    </Paper>
                  ))}
                </Stack>
              </CardContent>
            </Card>
          )}
        </>
      )}
    </Box>
  );
};

export default JobSearch; 